import type { ReactNode } from 'react';
import { Bars, Card, CardHeader, CardTitle, delta, type Bar } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { Usage, UsageCompany } from './usage-types';
import { deviceWord, featureWord, pathWords, screenWord } from './words';
import { CompanyName } from '../TestMark';
import '../admin.css';

/** Where a bar leads: the companies behind a feature or a screen, or one company's page. */
export type UsageTarget = { feature: string } | { screen: string } | { tenant: string };

export interface UsageBoardProps {
  usage: Usage;
  /** The router's link, so every number opens its companies. */
  renderLink: (target: UsageTarget, children: ReactNode) => ReactNode;
}

const TOP = 12;

/** A whole percent, never above 100: a company can use a feature in a period it never signed in or read a page in. */
function share(part: number, whole: number): number {
  return whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0;
}

/** The chart layer links by href string; the board keeps the target in the string and decodes it. */
function encode(target: UsageTarget): string {
  return JSON.stringify(target);
}

function decode(href: string): UsageTarget {
  return JSON.parse(href) as UsageTarget;
}

function featureBars(usage: Usage, f: Formatter): Bar[] {
  return usage.features.slice(0, TOP).map((x) => ({
    label: featureWord(x.feature),
    value: share(x.companies, usage.activeCompanies),
    previous: share(x.previousCompanies, usage.previousActiveCompanies),
    valueText: `${f.int(share(x.companies, usage.activeCompanies))}% · ${f.int(x.companies)}`,
    href: encode({ feature: x.feature }),
  }));
}

function screenBars(usage: Usage, f: Formatter): Bar[] {
  // A screen nobody spent a minute on is not "where time goes"; a glance shows as under a minute.
  return usage.screens
    .filter((x) => x.minutes >= 1 || x.visits > 0)
    .slice(0, TOP)
    .map((x) => ({
      label: screenWord(x.screen),
      value: Math.round(x.minutes),
      previous: Math.round(x.previousMinutes),
      valueText: x.minutes < 1 ? '< 1 min' : `${f.int(Math.round(x.minutes))} min`,
      href: encode({ screen: x.screen }),
    }));
}

function Strip({ usage, f }: { usage: Usage; f: Formatter }): ReactNode {
  const active = delta(usage.activeCompanies, usage.previousActiveCompanies, 'up');
  const devices = Object.entries(usage.devices).sort((a, b) => b[1] - a[1]);
  const sessions = devices.reduce((n, [, v]) => n + v, 0);
  return (
    <div className="ad-usage__strip">
      <span>
        <strong>{f.int(usage.activeCompanies)}</strong> active companies{' '}
        <span className={`ad-today__delta ad-today__delta--${active.tone}`}>{active.text}</span>{' '}
        <span className="ad-small">
          vs the period before. Active: a page read or a sign-in in the period.
        </span>
      </span>
      <span className="ad-small">
        {sessions === 0
          ? 'No sessions yet'
          : devices.map(([d, n]) => `${deviceWord(d)} ${f.int(share(n, sessions))}%`).join(' · ')}
      </span>
    </div>
  );
}

function Companies({
  usage,
  f,
  renderLink,
}: {
  usage: Usage;
  f: Formatter;
  renderLink: UsageBoardProps['renderLink'];
}): ReactNode {
  const drill = usage.companies;
  if (!drill) return null;
  const what = drill.feature
    ? featureWord(drill.feature).toLowerCase()
    : `opened ${screenWord(drill.screen)}`;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Companies that {what}</CardTitle>
      </CardHeader>
      {drill.rows.length === 0 ? <p className="ad-small">None this period.</p> : null}
      <ul className="ad-list">
        {drill.rows.map((c: UsageCompany) => (
          <li key={c.id} className="ad-row ad-row--between">
            <CompanyName name={renderLink({ tenant: c.slug }, c.name)} test={c.testCompany} />
            <span className="ad-small">{f.int(c.uses)} times</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

interface CardProps {
  usage: Usage;
  f: Formatter;
  link: (href: string, children: ReactNode) => ReactNode;
}

function FeaturesCard({ usage, f, link }: CardProps): ReactNode {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Features by adoption</CardTitle>
      </CardHeader>
      <p className="ad-small">
        Share of active companies that did each at least once; the period before ghosted behind.
      </p>
      {usage.features.length === 0 ? <p className="ad-small">No actions yet.</p> : null}
      <Bars
        bars={featureBars(usage, f)}
        label="Features by share of active companies"
        renderLink={link}
        format={(n) => `${n}%`}
      />
    </Card>
  );
}

function ScreensCard({ usage, f, link }: CardProps): ReactNode {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Screens by time</CardTitle>
      </CardHeader>
      <p className="ad-small">
        Minutes open inside a company, a visit counting ten at most (a tab left open is not use);
        the period before ghosted behind.
      </p>
      {usage.screens.length === 0 ? <p className="ad-small">No screen views yet.</p> : null}
      <Bars
        bars={screenBars(usage, f)}
        label="Screens by minutes open"
        renderLink={link}
        format={f.int}
      />
    </Card>
  );
}

function PathsCard({ usage, f }: { usage: Usage; f: Formatter }): ReactNode {
  return (
    <Card className="ad-usage__paths">
      <CardHeader>
        <CardTitle>Paths</CardTitle>
      </CardHeader>
      <p className="ad-small">
        Walks of two to five screens inside a company, by how many sessions took them.
      </p>
      {usage.paths.length === 0 ? <p className="ad-small">No walks yet.</p> : null}
      <ol className="ad-list">
        {usage.paths.map((p) => (
          <li key={p.screens.join('>')} className="ad-row ad-row--between">
            <span>{pathWords(p.screens)}</span>
            <span className="ad-small">
              {f.int(p.sessions)} {p.sessions === 1 ? 'session' : 'sessions'}
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** Three pictures: features by adoption, screens by time, the most common paths; every bar opens its companies. */
export function UsageBoard({ usage, renderLink }: UsageBoardProps): ReactNode {
  const f = useFormat();
  const link = (href: string, children: ReactNode) => renderLink(decode(href), children);
  return (
    <div className="ad-stack">
      <Strip usage={usage} f={f} />
      <Companies usage={usage} f={f} renderLink={renderLink} />
      <div className="ad-usage">
        <FeaturesCard usage={usage} f={f} link={link} />
        <ScreensCard usage={usage} f={f} link={link} />
        <PathsCard usage={usage} f={f} />
      </div>
    </div>
  );
}
